import { useSelector } from 'react-redux';
import { keepPreviousData } from '@tanstack/react-query';
import {
    PortfolioVoucherOverview,
    PortfolioVoucherOverviewFilters,
    useRepositoryMutation,
    useRepositoryQuery,
    VoucherCode,
} from '@mews-commander/data-access';
import { lt, showSuccessAlert, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { MewsTeam } from '@mews/enums';
import { TableState } from '@optimus-web/core';
import { getCurrentTargetEnterpriseIds } from '@mews-commander/legacy-entities/src/ducks/enterpriseRelationships';

interface UseDeleteVoucherCodesParameters {
    selectedVoucherCodes: VoucherCode[];
}

export const useCreateVoucher = (skipSuccessAlert?: boolean) =>
    useRepositoryMutation('vouchers', 'bulkAdd', {
        onSuccess: () => {
            if (!skipSuccessAlert) {
                showSuccessAlert({
                    title: lt('VoucherCreated'),
                });
            }
        },
        invalidateQueries: { portfolioVouchers: ['getOverviewEntries'] },
    });

export const useCreateVoucherCodes = ({ selectedVouchers }: { selectedVouchers: PortfolioVoucherOverview[] }) =>
    useRepositoryMutation('voucherCodes', 'bulkAdd', {
        onSuccess: () => {
            showSuccessAlert({
                description: lt('VoucherCodeAddSuccessTitle', {
                    count: selectedVouchers.length,
                    voucherName: selectedVouchers[0].name,
                }),
            });
        },
        invalidateQueries: { portfolioVouchers: ['getOverviewEntries'] },
    });

export const useDeleteVouchersMutation = () => {
    const localizeText = useLocalizeText();

    return useRepositoryMutation('vouchers', 'delete', {
        transformParams: (vouchers: PortfolioVoucherOverview[]) => ({
            voucherIds: vouchers.map((voucher) => voucher.id),
        }),
        onSuccess: (_, deletedVouchers) => {
            const localizedVoucherTextKey =
                deletedVouchers.length > 1 ? localizeText({ textKey: TextKey.VoucherPlural }) : deletedVouchers[0].name;

            showSuccessAlert({
                description: lt('DeleteSuccessMessage', { entity: localizedVoucherTextKey }),
            });
        },
        invalidateQueries: { portfolioVouchers: ['getOverviewEntries'] },
        meta: {
            logError: {
                team: MewsTeam.Pricing,
            },
        },
    });
};

export const useDeleteVoucherCodes = ({ selectedVoucherCodes }: UseDeleteVoucherCodesParameters) =>
    useRepositoryMutation('voucherCodes', 'delete', {
        onSuccess: () => {
            showSuccessAlert({
                title: lt('VoucherCodeDeleteSuccessTitle', { count: selectedVoucherCodes.length }),
                description: lt('VoucherCodeDeleteSuccessDescription', { count: selectedVoucherCodes.length }),
            });
        },
        invalidateQueries: { portfolioVouchers: ['getOverviewEntries'] },
    });

export const useEditVoucher = () =>
    useRepositoryMutation('vouchers', 'bulkUpdate', {
        onSuccess: ({ vouchers }) => {
            const isBulkUpdate = vouchers.length > 1;

            showSuccessAlert({
                title: lt(isBulkUpdate ? 'SavedChangesToVouchers' : 'SavedChangesToSingleVoucher', {
                    voucherName: vouchers[0].name || '',
                }),
                description: isBulkUpdate
                    ? lt('VouchersEditedCountMessage', { numberOfVouchers: vouchers.length })
                    : undefined,
            });
        },
        invalidateQueries: { portfolioVouchers: ['getOverviewEntries'], rates: ['getIntersectedKeys'] },
    });

export const useVoucherTableData = (
    tableState: TableState<PortfolioVoucherOverview, PortfolioVoucherOverviewFilters>
) => {
    const enterpriseIds = useSelector(getCurrentTargetEnterpriseIds);

    return useRepositoryQuery(
        'portfolioVouchers',
        'getOverviewEntries',
        { enterpriseIds, ...tableState },
        {
            placeholderData: keepPreviousData,
        }
    );
};
